<template>
  <div class="pb-8">
    <v-toolbar color="grey-darken-4" flat theme="dark" density="compact">
      <v-toolbar-title>{{ $t("home.inventory_intake.title") }}</v-toolbar-title>
    </v-toolbar>

    <v-card flat class="ma-2">
      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <v-autocomplete
              v-model="institutionId"
              :items="institutions"
              :label="$t('home.inventory_intake.select_institution')"
              :hint="$t('home.select_institution')"
              :loading="loadingInstitutions"
              variant="solo"
              item-title="title"
              item-value="_id"
              persistent-hint
              color="red-darken-2"
              clearable
            />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="timePeriod"
              :items="timePeriodItems"
              :label="$t('home.charts.time_period')"
              variant="solo"
              item-title="title"
              item-value="value"
              color="red-darken-2"
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-menu v-model="dateMenu" :close-on-content-click="false">
              <template #activator="{ props }">
                <v-text-field
                  v-bind="props"
                  :model-value="formattedDate"
                  :label="$t('home.charts.select_time_period')"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  hide-details
                  variant="solo"
                  color="red-darken-2"
                />
              </template>
              <v-date-picker
                v-model="pickerDate"
                :type="timePeriod === 'Month' ? 'month' : 'date'"
                color="red-darken-2"
                reactive
                @update:model-value="dateMenu = false"
              />
            </v-menu>
          </v-col>
          <v-col cols="12" md="3" class="d-flex align-center">
            <v-btn
              color="red-darken-3"
              variant="flat"
              :loading="loading"
              :disabled="!institutionId"
              @click="fetchIntake"
            >
              {{ $t("home.inventory_intake.load") }}
              <v-icon end>mdi-chart-bar</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="4">
            <v-autocomplete
              v-model="brand"
              :items="brands"
              :label="$t('home.inventory_intake.brand')"
              :loading="loadingBrands"
              variant="solo"
              item-title="title"
              item-value="brandCode"
              color="red-darken-2"
              hide-details
              clearable
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-autocomplete
              v-model="selectedModels"
              :items="models"
              :label="$t('home.inventory_intake.models')"
              :loading="loadingModels"
              :disabled="brand == null"
              variant="solo"
              item-title="description"
              item-value="modelCode"
              color="red-darken-2"
              hide-details
              multiple
              chips
              closable-chips
              clearable
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-autocomplete
              v-model="selectedVersions"
              :items="versions"
              :label="$t('home.inventory_intake.versions')"
              :loading="loadingVersions"
              :disabled="!selectedModels.length"
              variant="solo"
              item-title="title"
              item-value="value"
              color="red-darken-2"
              hide-details
              multiple
              chips
              closable-chips
              clearable
            />
          </v-col>
        </v-row>

        <div class="text-caption text-medium-emphasis mb-4">
          {{ $t("home.inventory_intake.hint") }}
        </div>

        <v-alert v-if="error" type="error" variant="tonal" border="bottom" class="mb-4">
          {{ error }}
        </v-alert>

        <v-row v-if="loading" justify="center" class="py-8">
          <v-progress-circular size="70" color="red" indeterminate />
        </v-row>

        <template v-else-if="hasData">
          <v-card color="red-darken-3" flat class="mb-4" max-width="280">
            <v-card-text class="pa-3">
              <div class="text-h5 font-weight-bold text-white">{{ total }}</div>
              <div class="text-body-2 text-white">
                {{ $t("home.inventory_intake.total") }}
                <span v-if="formattedDate"> · {{ formattedDate }}</span>
              </div>
            </v-card-text>
          </v-card>

          <v-card flat color="grey-lighten-4" class="pa-2">
            <chart
              :key="`intake-${chartKey}`"
              type="bar"
              :data="chartData"
              :height="280"
            />
          </v-card>
        </template>

        <v-alert v-else-if="!institutionId" type="info" variant="tonal" class="mt-2">
          {{ $t("home.inventory_intake.empty") }}
        </v-alert>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import moment from "moment";
import getRequest from "../../api/getRequest";
import Chart from "../chart.vue";

function formatLocalDateString(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatLocalMonthString(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

function parseLocalDateString(str, timePeriod) {
  if (timePeriod === "Month") {
    const [y, m] = str.split("-").map(Number);
    return new Date(y, m - 1, 1);
  }
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function todayLocalDateString() {
  return formatLocalDateString(new Date());
}

export default {
  components: { chart: Chart },
  data: () => ({
    institutionId: null,
    institutions: [],
    loadingInstitutions: false,
    timePeriod: "Week",
    time: todayLocalDateString(),
    dateMenu: false,
    brand: null,
    selectedModels: [],
    selectedVersions: [],
    brands: [],
    models: [],
    versions: [],
    loadingBrands: false,
    loadingModels: false,
    loadingVersions: false,
    loading: false,
    error: null,
    buckets: [],
    total: 0,
    chartKey: 0,
    hasLoaded: false,
    timePeriodItems: [
      { title: "Month", value: "Month" },
      { title: "Week", value: "Week" },
      { title: "Day", value: "Day" },
    ],
  }),
  computed: {
    formattedDate() {
      if (!this.time) return "";
      const date =
        this.time instanceof Date
          ? this.time
          : parseLocalDateString(this.time, this.timePeriod);
      if (Number.isNaN(date.getTime())) return "";
      return this.timePeriod === "Month"
        ? date.toLocaleDateString(undefined, { month: "short", year: "numeric" })
        : date.toLocaleDateString(undefined, {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
    },
    pickerDate: {
      get() {
        if (!this.time) return null;
        if (this.time instanceof Date) return this.time;
        return parseLocalDateString(this.time, this.timePeriod);
      },
      set(value) {
        if (!value || !(value instanceof Date)) return;
        this.time =
          this.timePeriod === "Month"
            ? formatLocalMonthString(value)
            : formatLocalDateString(value);
      },
    },
    hasData() {
      return this.hasLoaded && Array.isArray(this.buckets);
    },
    chartData() {
      const labels = (this.buckets || []).map((element) => {
        if (this.timePeriod === "Day") {
          return `${moment(element.startingTime).format("LT")} - ${moment(
            element.endingTime,
          ).format("LT")}`;
        }
        if (this.timePeriod === "Week") {
          return moment(element.startingTime).format("ddd, MMM D");
        }
        return moment(element.startingTime).format("D");
      });
      return {
        labels,
        datasets: [
          {
            label: "InventoryIntake",
            backgroundColor: "rgba(183, 28, 28, 0.85)",
            borderColor: "rgba(183, 28, 28, 1)",
            data: (this.buckets || []).map((element) => element.value),
          },
        ],
      };
    },
  },
  watch: {
    brand() {
      this.selectedModels = [];
      this.selectedVersions = [];
      this.models = [];
      this.versions = [];
      if (this.brand != null) this.getModels();
    },
    selectedModels: {
      deep: true,
      handler() {
        if (!this.selectedModels.length) {
          this.selectedVersions = [];
          this.versions = [];
          return;
        }
        this.getVersions();
      },
    },
  },
  mounted() {
    this.timePeriodItems[0].title = this.$t("home.charts.time_period_items.month");
    this.timePeriodItems[1].title = this.$t("home.charts.time_period_items.week");
    this.timePeriodItems[2].title = this.$t("home.charts.time_period_items.day");
    this.getInstitutions();
    this.getBrands();
  },
  methods: {
    getApiTime() {
      let m;
      if (this.timePeriod === "Month") {
        const monthValue =
          typeof this.time === "string" && this.time.length === 7
            ? `${this.time}-01`
            : this.time;
        m = moment(monthValue, "YYYY-MM-DD").startOf("month");
      } else {
        m = moment(this.time, "YYYY-MM-DD").startOf(
          this.timePeriod === "Week" ? "week" : "day",
        );
      }
      return m.toISOString();
    },
    getInstitutions() {
      this.loadingInstitutions = true;
      getRequest("/institution/")
        .then((response) => {
          this.institutions = response.institutions || [];
        })
        .catch(() => {
          this.institutions = [];
        })
        .finally(() => {
          this.loadingInstitutions = false;
        });
    },
    getBrands() {
      this.loadingBrands = true;
      getRequest("/info-car/brands")
        .then((response) => {
          this.brands = response.brands || [];
        })
        .catch(() => {
          this.brands = [];
        })
        .finally(() => {
          this.loadingBrands = false;
        });
    },
    getModels() {
      this.loadingModels = true;
      getRequest("/info-car/models", `brand_code=${this.brand}`)
        .then((response) => {
          this.models = response.models || [];
        })
        .catch(() => {
          this.models = [];
        })
        .finally(() => {
          this.loadingModels = false;
        });
    },
    async getVersions() {
      this.loadingVersions = true;
      try {
        const results = await Promise.all(
          this.selectedModels.map((modelCode) =>
            getRequest(
              "/info-car/versions",
              `brand_code=${this.brand}&model_code=${modelCode}`,
            ).then((response) => ({
              modelCode,
              versions: response.versions || [],
            })),
          ),
        );
        const modelTitles = {};
        this.models.forEach((model) => {
          modelTitles[model.modelCode] = model.description;
        });
        const items = [];
        results.forEach(({ modelCode, versions }) => {
          versions.forEach((version) => {
            const description = version.fullDescription || String(version.versionCode);
            items.push({
              value: `${modelCode}:${version.versionCode}`,
              title:
                this.selectedModels.length > 1
                  ? `${modelTitles[modelCode] || modelCode} — ${description}`
                  : description,
            });
          });
        });
        this.versions = items;
        this.selectedVersions = this.selectedVersions.filter((value) =>
          items.some((item) => item.value === value),
        );
      } catch (err) {
        this.versions = [];
      } finally {
        this.loadingVersions = false;
      }
    },
    async fetchIntake() {
      const role = this.$store.getters.role;
      if (role !== "LekoAdmin" && role !== "SuperAdmin") return;
      if (!this.institutionId) return;

      this.loading = true;
      this.error = null;
      try {
        const params = [
          `institution_id=${this.institutionId}`,
          `period=${this.timePeriod}`,
          `time=${encodeURIComponent(this.getApiTime())}`,
        ];
        if (this.brand != null && this.brand !== "") {
          params.push(`brand=${this.brand}`);
        }
        if (this.selectedModels.length) {
          params.push(`models=${this.selectedModels.join(",")}`);
        }
        if (this.selectedVersions.length) {
          params.push(
            `versions=${encodeURIComponent(this.selectedVersions.join(","))}`,
          );
        }
        const response = await getRequest(
          "/history/admin/inventory-intake",
          params.join("&"),
        );
        this.buckets = response.buckets || [];
        this.total = response.total || 0;
        this.hasLoaded = true;
        this.chartKey += 1;
      } catch (err) {
        this.buckets = [];
        this.total = 0;
        this.hasLoaded = false;
        if (err?.response?.status === 403) {
          this.error = this.$t("home.inventory_intake.not_authorized");
        } else {
          this.error = this.$t("home.inventory_intake.error");
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
